/**
 * Match detail page - dimension breakdown, recommended instrument,
 * approach strategy, talking points, and status workflow.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Trash2,
  CheckCircle2,
  Target,
  Handshake,
  XCircle,
  Sparkles,
  Layers,
  FileText,
  RefreshCw,
  Send,
  Lightbulb,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import DimensionScoreBar from '../components/DimensionScoreBar';
import SuccessPredictionPanel from '../components/SuccessPredictionPanel';
import ScoringMathBreakdown from '../components/ScoringMathBreakdown';
import DealBriefView from '../components/DealBriefView';
import MatchOutreachPanel from '../components/MatchOutreachPanel';
import MatchConsensusPanel from '../components/MatchConsensusPanel';
import MatchStatusFeedbackModal from '../components/MatchStatusFeedbackModal';
import matchService from '../services/matchService';
import api from '../services/api';
import dealPipelineService from '../services/dealPipelineService';
import dealBriefService from '../services/dealBriefService';
import { Match, MatchStatus, AiReview, AiReviewVerdict, AiReviewConfidence } from '../types/match';
import { DealBrief } from '../types/dealBrief';

const formatLabel = (value?: string): string => {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const scoreColor = (score: number): string => {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-yellow-600';
  return 'text-red-600';
};

const statusBadgeClasses = (status: MatchStatus): string => {
  switch (status) {
    case 'introduced':
      return 'bg-purple-100 text-purple-800';
    case 'pursuing':
      return 'bg-blue-100 text-blue-800';
    case 'reviewed':
      return 'bg-yellow-100 text-yellow-800';
    case 'passed':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const VERDICT_BADGE: Record<AiReviewVerdict, string> = {
  strong_fit: 'bg-green-100 text-green-800 border-green-300',
  good_fit: 'bg-blue-100 text-blue-800 border-blue-300',
  marginal_fit: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  poor_fit: 'bg-orange-100 text-orange-800 border-orange-300',
  no_fit: 'bg-red-100 text-red-800 border-red-300',
};

const VERDICT_LABEL: Record<AiReviewVerdict, string> = {
  strong_fit: 'Strong fit',
  good_fit: 'Good fit',
  marginal_fit: 'Marginal fit',
  poor_fit: 'Poor fit',
  no_fit: 'No fit',
};

const CONFIDENCE_BADGE: Record<AiReviewConfidence, string> = {
  high: 'bg-emerald-100 text-emerald-800',
  medium: 'bg-slate-100 text-slate-700',
  low: 'bg-amber-100 text-amber-800',
};

const formatGeneratedAgo = (iso?: string | null): string => {
  if (!iso) return '';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '';
  const diffMs = Date.now() - ts;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

// MUST MATCH backend/app/services/matching/scoring.py DEFAULT_WEIGHTS.
// These are the published per-dimension weights used to combine the
// 7 dimension scores into the overall 0-100 match score. When the
// instrument dimension doesn't apply (Company target), the backend
// drops it and re-normalises the remaining 6 to sum to 1.0.
const DIMENSIONS: Array<{ key: string; label: string; weight: number }> = [
  { key: 'stage', label: 'Stage', weight: 0.25 },
  { key: 'sector', label: 'Sector', weight: 0.20 },
  { key: 'check_size', label: 'Check Size', weight: 0.15 },
  { key: 'instrument', label: 'Instrument', weight: 0.15 },
  { key: 'geography', label: 'Geography', weight: 0.10 },
  { key: 'thesis', label: 'Thesis', weight: 0.10 },
  { key: 'co_investment', label: 'Co-Investment', weight: 0.05 },
];

// Per-dimension guidance for the "How to improve this match" panel.
// Tells the user which fields drive each dimension and what to do when
// the score is low. Numbers under 60 are flagged as "needs work";
// 60–79 as "could be better".
const IMPROVEMENT_GUIDANCE: Record<
  string,
  { fixesOnInvestor: string[]; fixesOnStartup: string[]; rationale: string }
> = {
  stage: {
    rationale:
      "Driven by the investor's stage_preferences and the company's funding_stage. A mismatch (e.g., investor invests at Series A but company is pre-seed) drops this dimension hard.",
    fixesOnInvestor: [
      "Update stage_preferences to include this stage if it's a fit",
      "Run research to fill in stage_preferences from the investor's portfolio history",
    ],
    fixesOnStartup: [
      "Verify funding_stage is current (pre_seed / seed / series_a / etc.)",
      "If a Series A is closing, update funding_stage so the next match scores correctly",
    ],
  },
  sector: {
    rationale:
      "Driven by sector_focus on the investor and industry / sub_industry on the company. Token-level matching — exact label fits score highest.",
    fixesOnInvestor: [
      "Add the company's sector to investor.sector_focus",
      "Re-run investor research to surface sector signals from their portfolio",
    ],
    fixesOnStartup: [
      "Make sure industry uses a canonical label (e.g., 'enterprise SaaS' not 'B2B software stuff')",
      "Add a more specific sub_industry — niche fits boost the score",
    ],
  },
  check_size: {
    rationale:
      "Driven by investor.check_size_min/max vs. company.funding_amount_sought. If the company is raising outside the investor's typical band, this scores low.",
    fixesOnInvestor: [
      "Verify check_size_min and check_size_max — outdated bands kill matches",
      "Run research to surface recent check sizes from Form D filings",
    ],
    fixesOnStartup: [
      "Set funding_amount_sought to the actual round size",
      "If the round can be split, update the per-investor check expectation in the deal",
    ],
  },
  instrument: {
    rationale:
      "Driven by which financial instruments the investor prefers (SAFEs, priced equity, etc.) vs. what the company is offering. Only applies to project-level matches.",
    fixesOnInvestor: [
      "Update instrument_preferences to reflect what they actually do",
    ],
    fixesOnStartup: [
      "If at the project level, set the project's instrument explicitly",
    ],
  },
  geography: {
    rationale:
      "Driven by investor.geographic_focus vs. company.headquarters / location_state. Geographic mandate mismatches (e.g., US-only investor on a UK company) drop the score.",
    fixesOnInvestor: [
      "Set geographic_focus accurately — 'global' if they invest anywhere",
    ],
    fixesOnStartup: [
      "Confirm headquarters and location_state are populated",
    ],
  },
  thesis: {
    rationale:
      "Driven by investment_philosophy on the investor (extracted from media + filings) vs. the company's description, business_model, and competitive_advantages.",
    fixesOnInvestor: [
      "Generate Claude dossier — pulls philosophy from videos, podcasts, articles",
      "Add manual notes to investor.investment_philosophy if Claude is off",
    ],
    fixesOnStartup: [
      "Tighten the company description — clearer one-liner = better thesis match",
      "Fill in business_model and competitive_advantages so the matcher has real text to compare",
    ],
  },
  co_investment: {
    rationale:
      "Driven by which other investors in the system have invested alongside this one. Sparse for newly-imported investors.",
    fixesOnInvestor: [
      "Run research to surface portfolio + co-investment history",
      "Import their co-investors so the graph has nodes to connect",
    ],
    fixesOnStartup: [
      "Add the company's existing investors as records — co-investment density boosts every match for the company",
    ],
  },
};


interface HowToImproveProps {
  dimensionScores: Record<string, number | null | undefined>;
  investorId: number | null | undefined;
  startupId: number | null | undefined;
  projectId: number | null | undefined;
  matchId: number;
}

const HowToImproveMatchPanel: React.FC<HowToImproveProps> = ({
  dimensionScores,
  investorId,
  startupId,
  projectId,
  matchId,
}) => {
  // Group dimensions: weak (<60), could-be-better (60–79), strong (≥80).
  const ranked = DIMENSIONS.map((d) => {
    const v = dimensionScores[d.key];
    return {
      ...d,
      score: typeof v === 'number' ? v : null,
      tier: v == null ? 'na' : v < 60 ? 'weak' : v < 80 ? 'meh' : 'strong',
    };
  });
  const weak = ranked.filter((r) => r.tier === 'weak');
  const meh = ranked.filter((r) => r.tier === 'meh');

  const [rescoring, setRescoring] = useState(false);

  const handleRescore = async () => {
    setRescoring(true);
    try {
      await api.post(`/matches/${matchId}/rescore`);
      toast.success('Match re-scored. Refreshing…');
      window.location.reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Re-score failed');
    } finally {
      setRescoring(false);
    }
  };

  return (
    <section className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-amber-500" />
            How to improve this match
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Each dimension is scored from real fields on the investor and
            company records. Fix the underlying field, click Re-score, and
            the overall score lifts on the next pass.
          </p>
        </div>
        <button
          onClick={handleRescore}
          disabled={rescoring}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 flex-shrink-0"
        >
          {rescoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Re-score
        </button>
      </div>

      {weak.length === 0 && meh.length === 0 ? (
        <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3">
          ✓ Every dimension is scoring 80 or above. Nothing to fix here — this
          is as strong as the data will support.
        </p>
      ) : (
        <div className="space-y-3">
          {[...weak, ...meh].map((r) => {
            const guidance = IMPROVEMENT_GUIDANCE[r.key];
            if (!guidance) return null;
            return (
              <div
                key={r.key}
                className={`border rounded-lg p-4 ${
                  r.tier === 'weak'
                    ? 'border-red-200 bg-red-50'
                    : 'border-amber-200 bg-amber-50'
                }`}
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">
                      {r.label}
                    </span>
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded ${
                        r.tier === 'weak'
                          ? 'bg-red-200 text-red-900'
                          : 'bg-amber-200 text-amber-900'
                      }`}
                    >
                      Score: {r.score ?? '—'} / 100
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {(r.weight * 100).toFixed(0)}% of overall
                  </span>
                </div>
                <p className="text-xs text-gray-700 mb-2 leading-relaxed">
                  {guidance.rationale}
                </p>
                <div className="grid md:grid-cols-2 gap-3 text-xs">
                  {investorId && (
                    <div>
                      <div className="font-semibold text-gray-800 mb-1">
                        On the investor side:
                      </div>
                      <ul className="space-y-1 text-gray-700">
                        {guidance.fixesOnInvestor.map((f, i) => (
                          <li key={i}>• {f}</li>
                        ))}
                      </ul>
                      <Link
                        to={`/investors/${investorId}`}
                        className="inline-block mt-2 text-indigo-700 hover:underline"
                      >
                        Open investor →
                      </Link>
                    </div>
                  )}
                  {(startupId || projectId) && (
                    <div>
                      <div className="font-semibold text-gray-800 mb-1">
                        On the {projectId ? 'project' : 'company'} side:
                      </div>
                      <ul className="space-y-1 text-gray-700">
                        {guidance.fixesOnStartup.map((f, i) => (
                          <li key={i}>• {f}</li>
                        ))}
                      </ul>
                      <Link
                        to={
                          projectId
                            ? `/projects/${projectId}`
                            : `/companies/${startupId}`
                        }
                        className="inline-block mt-2 text-indigo-700 hover:underline"
                      >
                        Open {projectId ? 'project' : 'company'} →
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500 leading-relaxed">
        <strong>Tip:</strong> the autonomous match scheduler re-scores every
        match every 168 hours. After you update a field on either side,
        click <strong>Re-score</strong> above to lift this match
        immediately rather than waiting for the next scheduled pass.
      </div>
    </section>
  );
};

const STATUS_ACTIONS: Array<{
  status: MatchStatus;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  className: string;
}> = [
  {
    status: 'reviewed',
    label: 'Mark Reviewed',
    icon: CheckCircle2,
    className: 'border-yellow-300 text-yellow-700 hover:bg-yellow-50',
  },
  {
    status: 'pursuing',
    label: 'Mark Pursuing',
    icon: Target,
    className: 'border-blue-300 text-blue-700 hover:bg-blue-50',
  },
  {
    status: 'introduced',
    label: 'Mark Introduced',
    icon: Handshake,
    className: 'border-purple-300 text-purple-700 hover:bg-purple-50',
  },
  {
    status: 'passed',
    label: 'Mark Passed',
    icon: XCircle,
    className: 'border-red-300 text-red-700 hover:bg-red-50',
  },
];

interface ClaudesTakeCardProps {
  review: AiReview | null;
  generatedAt: string | null;
  generating: boolean;
  onGenerate: () => void;
}

const ClaudesTakeCard: React.FC<ClaudesTakeCardProps> = ({
  review,
  generatedAt,
  generating,
  onGenerate,
}) => {
  return (
    <section className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-500" />
            Claude's Take
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Qualitative review of this match — strengths, concerns, and a
            recommended next step.
          </p>
        </div>
        <button
          onClick={onGenerate}
          disabled={generating}
          className="inline-flex items-center px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 flex-shrink-0"
        >
          {generating ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : review ? (
            <RefreshCw className="w-4 h-4 mr-1.5" />
          ) : (
            <Sparkles className="w-4 h-4 mr-1.5" />
          )}
          {review ? 'Regenerate' : 'Generate Review'}
        </button>
      </div>

      {!review ? (
        <div className="text-sm text-gray-500 py-6 text-center border border-dashed border-gray-200 rounded-lg">
          Claude review not yet generated. Click "Generate Review" for a
          qualitative analysis of this match.
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`px-2.5 py-1 text-xs font-medium rounded-full border ${
                VERDICT_BADGE[review.fit_verdict] ||
                'bg-gray-100 text-gray-800 border-gray-300'
              }`}
            >
              {VERDICT_LABEL[review.fit_verdict] || review.fit_verdict}
            </span>
            <span
              className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                CONFIDENCE_BADGE[review.confidence] ||
                'bg-gray-100 text-gray-700'
              }`}
            >
              {review.confidence} confidence
            </span>
            {typeof review.suggested_score_adjustment === 'number' && (
              <span className="text-xs text-gray-600">
                Score adjustment:{' '}
                <span
                  className={
                    review.suggested_score_adjustment > 0
                      ? 'text-green-700 font-medium'
                      : review.suggested_score_adjustment < 0
                      ? 'text-red-700 font-medium'
                      : 'text-gray-700'
                  }
                >
                  {review.suggested_score_adjustment > 0 ? '+' : ''}
                  {review.suggested_score_adjustment}
                </span>
              </span>
            )}
            {generatedAt && (
              <span className="text-xs text-gray-400 ml-auto">
                Generated {formatGeneratedAgo(generatedAt)}
              </span>
            )}
          </div>

          {review.headline && (
            <p className="text-base font-medium text-gray-900">
              {review.headline}
            </p>
          )}

          {review.reasoning && (
            <p className="text-sm text-gray-700 italic whitespace-pre-wrap">
              {review.reasoning}
            </p>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {review.strengths && review.strengths.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">
                  Strengths
                </h4>
                <ul className="space-y-1.5 text-sm text-gray-800">
                  {review.strengths.map((s, i) => (
                    <li key={i} className="flex items-start">
                      <CheckCircle2 className="w-4 h-4 mr-1.5 mt-0.5 text-green-600 flex-shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {review.concerns && review.concerns.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">
                  Concerns
                </h4>
                <ul className="space-y-1.5 text-sm text-gray-800">
                  {review.concerns.map((c, i) => (
                    <li key={i} className="flex items-start">
                      <XCircle className="w-4 h-4 mr-1.5 mt-0.5 text-red-600 flex-shrink-0" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {review.missing_info && review.missing_info.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                Missing info
              </h4>
              <ul className="space-y-1 text-sm text-gray-600">
                {review.missing_info.map((m, i) => (
                  <li key={i} className="flex items-start">
                    <span className="mr-2 text-gray-400">•</span>
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {review.suggested_action && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1">
                Suggested action
              </div>
              <p className="text-sm text-amber-900">{review.suggested_action}</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
};


const MatchDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [match, setMatch] = useState<Match | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [updating, setUpdating] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [promoting, setPromoting] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'brief'>('overview');
  const [brief, setBrief] = useState<DealBrief | null>(null);
  const [briefLoading, setBriefLoading] = useState<boolean>(false);
  const [briefGenerating, setBriefGenerating] = useState<boolean>(false);
  const [reviewGenerating, setReviewGenerating] = useState<boolean>(false);
  const [requestingIntro, setRequestingIntro] = useState<boolean>(false);

  useEffect(() => {
    const matchId = Number(id);
    if (!matchId) {
      toast.error('Invalid match id');
      setIsLoading(false);
      return;
    }
    const load = async () => {
      setIsLoading(true);
      const result = await matchService.fetchMatch(matchId);
      if (result.error) {
        toast.error(result.error);
      } else if (result.data) {
        setMatch(result.data);
      }
      setIsLoading(false);
    };
    load();

    // Preload latest brief (silent — 404 is fine, brief may not exist yet)
    const loadBrief = async () => {
      setBriefLoading(true);
      const result = await dealBriefService.fetchLatestBrief(matchId);
      if (result.data) setBrief(result.data);
      setBriefLoading(false);
    };
    loadBrief();
  }, [id]);

  const handleGenerateBrief = async () => {
    if (!match) return;
    setBriefGenerating(true);
    const enqueueResult = await dealBriefService.generateBrief(match.id);
    if (enqueueResult.error) {
      setBriefGenerating(false);
      toast.error(enqueueResult.error);
      return;
    }

    // The /generate endpoint returns 202 with a queue ack
    // ({queued: true, message: ...}), not the brief itself — Claude
    // generation takes 15-30s in a background task. Poll the latest
    // brief until a new version lands or we hit a 90s cap.
    toast('Brief generation started — this takes 15-30 seconds…', {
      icon: '⏳',
      duration: 4000,
    });
    const baselineVersion = brief?.version ?? 0;
    const startedAt = Date.now();
    const HARD_CAP_MS = 90_000;
    const pollOnce = async (): Promise<boolean> => {
      const r = await dealBriefService.fetchLatestBrief(match.id);
      if (r.data && (r.data.version ?? 0) > baselineVersion) {
        setBrief(r.data);
        toast.success(
          baselineVersion > 0
            ? `Brief regenerated (v${r.data.version})`
            : 'Deal Brief ready',
        );
        return true;
      }
      return false;
    };
    const poll = async () => {
      const elapsed = Date.now() - startedAt;
      if (elapsed > HARD_CAP_MS) {
        setBriefGenerating(false);
        toast.error(
          'Brief generation took longer than 90s. Refresh the page in a minute to see it.',
          { duration: 8000 },
        );
        return;
      }
      const landed = await pollOnce();
      if (landed) {
        setBriefGenerating(false);
        return;
      }
      // Backoff: 4s for first 30s, then 6s.
      const interval = elapsed < 30_000 ? 4_000 : 6_000;
      window.setTimeout(poll, interval);
    };
    window.setTimeout(poll, 5_000);
  };

  const handleGenerateReview = async () => {
    if (!match) return;
    setReviewGenerating(true);
    const result = await matchService.requestMatchReview(match.id);
    if (result.error) {
      setReviewGenerating(false);
      toast.error(result.error);
      return;
    }
    toast.success('Claude review queued — refreshing in a few seconds...');
    // Poll for the review to land — the backend returns 202 immediately and
    // the actual generation happens in a background task.
    const start = Date.now();
    const poll = async () => {
      const next = await matchService.fetchMatch(match.id);
      const updated = next.data;
      const previousTs = match.ai_review_generated_at || '';
      const newTs = updated?.ai_review_generated_at || '';
      if (updated && newTs && newTs !== previousTs) {
        setMatch(updated);
        setReviewGenerating(false);
        toast.success("Claude's review is in.");
        return;
      }
      if (Date.now() - start > 60000) {
        setReviewGenerating(false);
        toast(
          "Review is still generating — refresh in a moment.",
          { icon: 'i' },
        );
        return;
      }
      setTimeout(poll, 3000);
    };
    setTimeout(poll, 3000);
  };

  const handleRequestIntro = async () => {
    if (!match) return;
    setRequestingIntro(true);
    const result = await matchService.requestMatchIntro(match.id);
    setRequestingIntro(false);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    // Backend logs the request and creates a pipeline row even when the
    // email notification fails (e.g. Resend domain not verified). Be
    // explicit about which side actually fired so the operator isn't
    // confused by a silent failure.
    const data = result.data as any;
    if (data?.notification_sent === false) {
      toast(
        'Intro request logged in pipeline. Email notification could not be sent — verify your sender domain at resend.com/domains.',
        { icon: 'ℹ️', duration: 9000 },
      );
    } else {
      toast.success('Intro requested — admin has been notified by email');
    }
    const next = await matchService.fetchMatch(match.id);
    if (next.data) setMatch(next.data);
  };

  // Status changes that benefit from structured feedback (pursuing /
  // passed / introduced) open a modal first; reviewed advances
  // immediately. The modal calls applyStatusChange with the
  // collected reasons + note when the user submits or skips.
  const [feedbackModalStatus, setFeedbackModalStatus] = useState<MatchStatus | null>(null);

  const STATUSES_WITH_FEEDBACK: MatchStatus[] = ['pursuing', 'passed', 'introduced'];

  const handleStatusChange = async (next: MatchStatus) => {
    if (!match) return;
    if (STATUSES_WITH_FEEDBACK.includes(next)) {
      setFeedbackModalStatus(next);
      return;
    }
    await applyStatusChange(next, [], '');
  };

  const applyStatusChange = async (
    next: MatchStatus,
    reasons: string[],
    note: string,
  ) => {
    if (!match) return;
    setUpdating(true);
    // matchService.updateMatchStatus is the legacy single-field
    // helper. To carry feedback we call the generic PATCH directly.
    try {
      const r = await api.patch(`/matches/${match.id}`, {
        status: next,
        ...(reasons.length > 0 ? { status_feedback_reasons: reasons } : {}),
        ...(note ? { status_feedback_note: note } : {}),
      });
      setMatch(r.data);
      const summary =
        reasons.length > 0
          ? `Status: ${formatLabel(next)} (${reasons.length} reason${reasons.length === 1 ? '' : 's'} captured)`
          : `Status: ${formatLabel(next)}`;
      toast.success(summary);
      setFeedbackModalStatus(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handlePromote = async () => {
    if (!match) return;
    setPromoting(true);
    const result = await dealPipelineService.createPipeline({ match_id: match.id });
    setPromoting(false);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    toast.success('Promoted to deal pipeline');
    if (result.data?.id) {
      navigate(`/deal-pipeline/${result.data.id}`);
    } else {
      navigate('/deal-pipeline');
    }
  };

  const handleDelete = async () => {
    if (!match) return;
    if (!window.confirm('Delete this match? This cannot be undone.')) return;
    setIsDeleting(true);
    const result = await matchService.deleteMatch(match.id);
    setIsDeleting(false);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    toast.success('Match deleted');
    navigate('/matches');
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
          <Loader2 className="w-5 h-5 mr-3 animate-spin" />
          Loading match...
        </div>
      </DashboardLayout>
    );
  }

  if (!match) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-600">
          <p className="mb-4">Match not found.</p>
          <Link to="/matches" className="text-blue-600 hover:underline">
            Back to matches
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const score = match.overall_score ?? 0;
  const targetName =
    match.project?.project_name ||
    match.startup?.name ||
    (match.project_id
      ? `Project #${match.project_id}`
      : match.startup_id
      ? `Startup #${match.startup_id}`
      : '—');

  const investorName =
    match.investor?.full_name || `Investor #${match.investor_id}`;
  const investorFirm = match.investor?.firm_name;

  const investorHref = `/investors/${match.investor_id}`;
  const targetHref = match.project_id
    ? `/projects/${match.project_id}`
    : match.startup_id
    ? `/companies/${match.startup_id}`
    : undefined;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Link
            to="/matches"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to matches
          </Link>
          <div className="flex gap-2">
            <button
              onClick={handlePromote}
              disabled={promoting}
              className="inline-flex items-center px-3 py-1.5 border border-blue-300 rounded-lg text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
            >
              {promoting ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Layers className="w-4 h-4 mr-1.5" />
              )}
              Promote to Pipeline
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="inline-flex items-center px-3 py-1.5 border border-red-300 rounded-lg text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>

        {/* Title block */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center gap-6">
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wide text-gray-500">
                Investor
              </div>
              <Link
                to={investorHref}
                className="text-xl font-semibold text-gray-900 hover:text-blue-600 truncate block"
              >
                {investorName}
              </Link>
              {investorFirm && (
                <div className="text-sm text-gray-600 truncate">{investorFirm}</div>
              )}
            </div>

            <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />

            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wide text-gray-500">
                {match.project_id ? 'Project' : 'Startup'}
              </div>
              {targetHref ? (
                <Link
                  to={targetHref}
                  className="text-xl font-semibold text-gray-900 hover:text-blue-600 truncate block"
                >
                  {targetName}
                </Link>
              ) : (
                <div className="text-xl font-semibold text-gray-900 truncate">
                  {targetName}
                </div>
              )}
            </div>

            <div className="flex-shrink-0 text-right">
              <div className={`text-5xl font-bold leading-none ${scoreColor(score)}`}>
                {Math.round(score)}
              </div>
              <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">
                Overall Score
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span
              className={`px-2.5 py-1 text-xs font-medium rounded-full ${statusBadgeClasses(
                match.status
              )}`}
            >
              {formatLabel(match.status)}
            </span>
            <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700">
              {formatLabel(match.match_type)}
            </span>
            {match.reviewed_at && (
              <span className="text-xs text-gray-500">
                Reviewed {new Date(match.reviewed_at).toLocaleDateString()}
                {match.reviewed_by ? ` by ${match.reviewed_by}` : ''}
              </span>
            )}
          </div>
        </div>

        {/* Tab bar */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-4">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === 'overview'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('brief')}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${
                activeTab === 'brief'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileText className="w-4 h-4" />
              Deal Brief
              {brief && (
                <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                  v{brief.version}
                </span>
              )}
            </button>
          </nav>
        </div>

        {activeTab === 'brief' ? (
          <>
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                AI-generated strategic brief for this match.
              </div>
              <button
                onClick={handleGenerateBrief}
                disabled={briefGenerating}
                className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
              >
                {briefGenerating ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : brief ? (
                  <RefreshCw className="w-4 h-4 mr-1.5" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-1.5" />
                )}
                {brief ? 'Regenerate' : 'Generate Deal Brief'}
              </button>
            </div>
            {briefLoading ? (
              <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
                <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                Loading brief...
              </div>
            ) : brief ? (
              <DealBriefView brief={brief} />
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-600">
                <FileText className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                <p className="font-medium text-gray-800 mb-1">
                  No deal brief yet
                </p>
                <p className="text-sm text-gray-500">
                  Click "Generate Deal Brief" to produce an AI strategic
                  analysis of this match.
                </p>
              </div>
            )}
          </>
        ) : (
        <>
        {/* Status workflow buttons */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-3">
            Advance status
          </div>
          <div className="flex flex-wrap gap-2">
            {STATUS_ACTIONS.map(({ status, label, icon: Icon, className }) => (
              <button
                key={status}
                onClick={() => handleStatusChange(status)}
                disabled={updating || match.status === status}
                className={`inline-flex items-center px-3 py-1.5 border rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
              >
                <Icon className="w-4 h-4 mr-1.5" />
                {label}
              </button>
            ))}
            {(match.status === 'suggested' || match.status === 'reviewed') &&
              !match.outreach_sent_at && (
                <button
                  onClick={handleRequestIntro}
                  disabled={requestingIntro}
                  className="inline-flex items-center px-3 py-1.5 border rounded-lg text-sm font-medium border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {requestingIntro ? (
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-1.5" />
                  )}
                  Request Introduction
                </button>
              )}
          </div>
        </div>

        {/* Introduction outreach (Cal.com booking CTA included) */}
        <MatchOutreachPanel
          match={match}
          onChanged={async () => {
            const next = await matchService.fetchMatch(match.id);
            if (next.data) setMatch(next.data);
          }}
        />

        {/* Multi-AI consensus check */}
        <MatchConsensusPanel match={match} />

        {/* Claude's Take */}
        <ClaudesTakeCard
          review={match.ai_review_json ?? null}
          generatedAt={match.ai_review_generated_at ?? null}
          generating={reviewGenerating}
          onGenerate={handleGenerateReview}
        />

        {/* Predictive success forecast */}
        <SuccessPredictionPanel
          matchId={match.id}
          prediction={match.success_prediction_json ?? null}
          generatedAt={match.success_prediction_at ?? null}
          onChanged={async () => {
            const next = await matchService.fetchMatch(match.id);
            if (next.data) setMatch(next.data);
          }}
        />

        {/* Dimension breakdown */}
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">
            Dimension breakdown
          </h3>
          <div className="space-y-3">
            {DIMENSIONS.map((d) => (
              <DimensionScoreBar
                key={d.key}
                label={d.label}
                score={match.dimension_scores?.[d.key]}
              />
            ))}
          </div>
        </section>

        {/* How to improve this match — practical guidance for the user.
            Surfaces which dimensions are weak and what to update on
            either side to lift the score on the next re-scoring pass. */}
        <HowToImproveMatchPanel
          dimensionScores={match.dimension_scores || {}}
          investorId={match.investor_id}
          startupId={match.startup_id}
          projectId={match.project_id}
          matchId={match.id}
        />

        {/* Math transparency: show how the dimension scores combine
            into the overall match score. Shows the formula, where the
            upside lives, and which dimensions are N/A (dropped + re-
            normalized for Company targets). */}
        <ScoringMathBreakdown
          title="How we computed this match score"
          overallScore={match.overall_score ?? 0}
          components={DIMENSIONS.map((d) => {
            const raw = match.dimension_scores?.[d.key];
            return {
              key: d.key,
              label: d.label,
              score: typeof raw === 'number' ? raw : null,
              weight: d.weight,
              notApplicable: raw === null || raw === undefined,
            };
          })}
        />

        {/* Recommended Instrument */}
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-blue-500" />
            Recommended instrument
          </h3>
          {match.recommended_instrument ? (
            <p className="text-base text-gray-900 font-medium">
              {formatLabel(match.recommended_instrument)}
            </p>
          ) : (
            <p className="text-sm text-gray-500">No instrument recommended.</p>
          )}
        </section>

        {/* Approach Strategy */}
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">
            Approach strategy
          </h3>
          {match.approach_strategy ? (
            <p className="whitespace-pre-wrap text-sm text-gray-800">
              {match.approach_strategy}
            </p>
          ) : (
            <p className="text-sm text-gray-500">No approach strategy yet.</p>
          )}
        </section>

        {/* Talking Points */}
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Talking points</h3>
          {match.talking_points && match.talking_points.length > 0 ? (
            <ul className="space-y-2 text-sm text-gray-800">
              {match.talking_points.map((tp, idx) => (
                <li key={idx} className="flex items-start">
                  <span className="mr-2 text-blue-500">•</span>
                  <span>{tp}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">No talking points generated.</p>
          )}
        </section>
        </>
        )}
      </div>

      <MatchStatusFeedbackModal
        open={feedbackModalStatus !== null}
        newStatus={feedbackModalStatus || 'reviewed'}
        onClose={() => setFeedbackModalStatus(null)}
        onSubmit={async (reasons, note) => {
          if (feedbackModalStatus) {
            await applyStatusChange(feedbackModalStatus, reasons, note);
          }
        }}
      />
    </DashboardLayout>
  );
};

export default MatchDetailPage;
